import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { LabelSelect, LabelInput } from '../../../components/Input/Input';
import { alertError, alertSuccess } from '../../../components/Toast';
import { formatCurrency } from '../../../core/utils/format';
import { loadBanks } from '../../../core/utils/bank';
import { markInstallmentAsPaid } from '../../../core/utils/installment';
import { INSTALLMENT_STATUS, PAYMENT_METHOD_OPTIONS, STATUS } from '../../../core/constants/financial';
import { todayISO } from '../../../core/utils/financialPeriod';
import FinancialFormModal from './FinancialFormModal';
import ReceiptUpload from './ReceiptUpload';

const PaymentModal = ({ isOpen, installment, mode = 'pagar', onClose, onConfirmed }) => {
  const company = useSelector((state) => state.app.company || {});
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState(null);
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  const isReceber = mode === 'receber';
  const title = isReceber ? 'Confirmar Recebimento' : 'Confirmar Pagamento';
  const bankLabel = isReceber ? 'Banco de Destino' : 'Banco para Débito';
  const dateLabel = isReceber ? 'Data do Recebimento' : 'Data do Pagamento';
  const methodLabel = isReceber ? 'Forma de Recebimento' : 'Forma de Pagamento';

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    loadBanks(company.id).then((data) => {
      if (cancelled) return;
      const active = (data || []).filter((b) => b.status === STATUS.ATIVO);
      setBanks(active);
      // Banco da parcela só entra se ainda ativo — o select não lista inativos.
      const prefill = installment?.bank_id;
      setBankId(active.some((b) => b.id === prefill) ? prefill : null);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, installment]);

  useEffect(() => {
    if (isOpen) {
      setPaymentDate(todayISO());
      setBankId(null);
      const stored = installment?.payment_method;
      setPaymentMethod(
        PAYMENT_METHOD_OPTIONS.some((o) => o.value === stored) ? stored : ''
      );
      setReceiptUrl(null);
    }
  }, [isOpen, installment]);

  const bankOptions = banks.map((b) => ({ value: b.id, label: b.name }));

  const handleConfirm = async () => {
    if (saving) return;
    if (!bankId) {
      alertError('Selecione um banco.');
      return;
    }
    if (!paymentMethod) {
      alertError(
        isReceber
          ? 'Selecione a forma de recebimento.'
          : 'Selecione a forma de pagamento.'
      );
      return;
    }
    if (!paymentDate) {
      alertError('Informe a data.');
      return;
    }

    setSaving(true);
    try {
      // Cobrança unificada de OS (linha agrupada): confirma todas as partes
      // pendentes de uma vez, com o mesmo banco/forma/data/comprovante.
      const targets = installment.children
        ? installment.children.filter((c) => c.status !== INSTALLMENT_STATUS.PAGO)
        : [installment];

      for (const target of targets) {
        const { error } = await markInstallmentAsPaid(target.id, {
          bank_id: bankId,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          receipt_url: receiptUrl,
        });
        if (error) {
          alertError('Erro ao confirmar.');
          return;
        }
      }
      alertSuccess(isReceber ? 'Recebimento confirmado!' : 'Pagamento confirmado!');
      if (onConfirmed) onConfirmed();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !installment) return null;

  const supplierClient = installment.financial_entry?.supplier_client || '—';

  return (
    <FinancialFormModal
      title={title}
      onClose={onClose}
      onSave={handleConfirm}
      saving={saving}
    >
      {/* Resumo da parcela */}
      <div style={{ background: '#2c2c2c', border: '1px solid #383838', borderRadius: 6, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ color: '#999' }}>{isReceber ? 'Cliente' : 'Fornecedor'} / Parcela</span>
          <span style={{ color: '#fff', fontWeight: 600 }}>
            {supplierClient} — {installment.installment_number}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ color: '#999' }}>Vencimento</span>
          <span style={{ color: '#e88c2a', fontWeight: 600 }}>
            {installment.due_date ? new Date(installment.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #383838', paddingTop: 8, marginTop: 4 }}>
          <span style={{ color: '#999', fontSize: 12, fontWeight: 600 }}>Valor</span>
          <span style={{ color: '#70E000', fontWeight: 700, fontSize: 16, fontFamily: "'Rajdhani', sans-serif" }}>
            {formatCurrency(installment.value)}
          </span>
        </div>
      </div>

      <LabelSelect menuPortalTarget={document.body}
        label={bankLabel}
        options={bankOptions}
        value={bankOptions.find((o) => o.value === bankId) || null}
        placeholder="Selecione"
        onChange={(opt) => setBankId(opt?.value || null)}
      />

      <LabelInput
        label={dateLabel}
        type="date"
        value={paymentDate}
        onChange={setPaymentDate}
      />

      <LabelSelect menuPortalTarget={document.body}
        label={methodLabel}
        options={PAYMENT_METHOD_OPTIONS}
        value={PAYMENT_METHOD_OPTIONS.find((o) => o.value === paymentMethod) || null}
        placeholder="Selecione"
        onChange={(opt) => setPaymentMethod(opt?.value || '')}
      />

      <ReceiptUpload value={receiptUrl} onChange={setReceiptUrl} bucket="installment" />

      <div style={{ background: 'rgba(91,196,36,0.08)', border: '1px solid rgba(91,196,36,0.25)', borderRadius: 4, padding: '9px 12px', fontSize: 11, color: '#ccc', lineHeight: 1.5 }}>
        Ao confirmar, esta parcela será marcada como{' '}
        <strong style={{ color: '#70E000' }}>{isReceber ? 'RECEBIDA' : 'PAGA'}</strong>{' '}
        e lançada no <strong style={{ color: '#60b4f6' }}>Extrato do Banco</strong>.
      </div>
    </FinancialFormModal>
  );
};

export default PaymentModal;
